import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Package, 
  ChevronDown, 
  ChevronUp, 
  User, 
  MapPin, 
  Building2, 
  Search, 
  Filter,
  UserCog,
  Users,
  Radio,
  Check,
  Loader2,
  PieChart,
  BarChart3,
  Activity,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Download,
  Upload,
  AlertCircle,
  Edit,
  FileText
} from 'lucide-react';
import { collection, getDocs, doc, updateDoc, writeBatch, setDoc, query, where, getDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { useLanguage } from '../contexts/LanguageContext';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { Modal } from '../components/common/Modal';

type PackageGroup = {
  id: string;
  name: string;
  customIndex?: string; // Changed to string since it can be text like "4-B"
  mainMakkah: {
    hotel: string;
    startDate: string;
    endDate: string;
  };
  shiftingMakkah: {
    hotel: string;
    startDate: string;
    endDate: string;
  } | null;
  madinah: {
    hotel: string;
    startDate: string;
    endDate: string;
  };
  packages: string[];
  pilgrimCount: number;
};

type GroupIndexMapping = {
  groupName: string;
  oldIndex: string;
  newIndex: string;
  status?: 'pending' | 'success' | 'error';
  message?: string;
};

type PilgrimOperation = {
  id: string;
  pilgrimId: string;
  pilgrimName: string;
  passportNumber: string;
  gender: 'male' | 'female';
  residenceCountry: string;
  packageName: string;
  guideId: string;
  guideName: string;
  organizerId: string;
  organizerName: string;
  operatorId?: string;
  operatorName?: string;
  operatorOrganizerId?: string; // Added field for operator's organizer ID
  operatorOrganizerName?: string; // Added field for operator's organizer name
  syncedAt: number;
  isOwner?: boolean;
};

type StatsData = {
  totalPilgrims: number;
  totalPackageGroups: number;
  totalOrganizers: number;
  totalPackages: number;
  assignedOperators: number;
  unassignedOperators: number;
  pilgrimsByGender: {
    male: number;
    female: number;
  };
  topCountries: Array<{name: string, count: number}>;
  topOrganizers: Array<{name: string, count: number}>;
};

type OrganizerPackageStats = {
  packageName: string;
  organizers: Array<{
    organizerId: string;
    organizerName: string;
    pilgrimCount: number;
  }>;
  totalPilgrims: number;
};

type Guide = {
  id: string;
  name: string;
  organizerId: string;
  email: string;
  [key: string]: any;
};

type OperationData = {
  operations: PilgrimOperation[];
  guides: Guide[];
  packages: PackageGroup[];
  stats: StatsData;
  organizerGuides: Record<string, Guide[]>;
  packageOrganizerStats: Record<string, OrganizerPackageStats[]>;
  groupOrganizers: Record<string, {id: string, name: string}[]>;
  uniqueOrganizers: {id: string, name: string}[];
  uniquePackages: string[];
  pilgrimsByPassport: Record<string, PilgrimOperation>;
  organizers: Record<string, any>; // Added organizers data
};

// Type for bulk upload
type BulkAssignment = {
  passportNumber: string;
  operatorName: string;
  status: 'pending' | 'success' | 'error';
  message?: string;
};

const ITEMS_PER_PAGE = 10;

const fetchOperationData = async (): Promise<OperationData> => {
  try {
    // First, fetch all guides and organizers
    const guidesSnapshot = await getDocs(collection(firestore, 'Guides'));
    const guidesData = guidesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Guide[];

    const organizersSnapshot = await getDocs(collection(firestore, 'Organizers'));
    const organizersData = organizersSnapshot.docs.reduce((acc, doc) => {
      acc[doc.id] = { id: doc.id, ...doc.data() };
      return acc;
    }, {} as Record<string, any>);

    const guidesMap: Record<string, Guide[]> = {};
    guidesData.forEach(guide => {
      if (guide.organizerId) {
        if (!guidesMap[guide.organizerId]) {
          guidesMap[guide.organizerId] = [];
        }
        guidesMap[guide.organizerId].push(guide);
      }
    });

    const operationsSnapshot = await getDocs(collection(firestore, 'operationlist'));
    const operations = operationsSnapshot.docs.map(doc => {
      const data = doc.data();
      let operationData: PilgrimOperation = {
        id: doc.id,
        ...data
      } as PilgrimOperation;

      // Add operator organizer information if missing
      if (data.operatorId && !data.operatorOrganizerId) {
        // Find the guide that matches the operatorId
        const operatorGuide = guidesData.find(guide => guide.id === data.operatorId);
        if (operatorGuide) {
          // Add operatorOrganizerId
          operationData.operatorOrganizerId = operatorGuide.organizerId;
          
          // Add operatorOrganizerName - get directly from organizersData
          const organizer = organizersData[operatorGuide.organizerId];
          if (organizer) {
            operationData.operatorOrganizerName = organizer.name;
          } else {
            operationData.operatorOrganizerName = "Unknown";
          }
        }
      }

      return operationData;
    });

    const pilgrimsByPassport: Record<string, PilgrimOperation> = {};
    operations.forEach(op => {
      if (op.passportNumber) {
        pilgrimsByPassport[op.passportNumber.toLowerCase()] = op;
      }
    });

    const orgMap = new Map<string, {id: string, name: string}>();
    operations.forEach(op => {
      if (!orgMap.has(op.organizerId)) {
        orgMap.set(op.organizerId, {
          id: op.organizerId,
          name: op.organizerName
        });
      }
    });
    const uniqueOrganizers = Array.from(orgMap.values());

    const uniquePackages = [...new Set(operations.map(op => op.packageName))].sort();

    const packagesSnapshot = await getDocs(collection(firestore, 'Packages'));
    const packagesData = packagesSnapshot.docs.reduce((acc, doc) => {
      acc[doc.id] = { id: doc.id, ...doc.data() };
      return acc;
    }, {} as Record<string, any>);

    // Fetch custom group indices from the database (single document)
    let groupIndicesMap: Record<string, string> = {};
    try {
      const referenceDoc = await getDoc(doc(firestore, 'packageGroupIndices', 'reference'));
      if (referenceDoc.exists()) {
        groupIndicesMap = referenceDoc.data() || {};
      }
    } catch (error) {
      console.error("Error fetching group indices:", error);
    }

    const packageGroups = processPackageGroups(packagesData, operations, groupIndicesMap);

    const stats = calculateStats(operations, packageGroups);
    const packageOrganizerStats = calculatePackageOrganizerStats(packageGroups, operations, guidesMap);
    const groupOrganizers = calculateGroupOrganizers(packageGroups, operations, guidesMap);

    return {
      operations,
      guides: guidesData,
      packages: packageGroups,
      stats,
      organizerGuides: guidesMap,
      packageOrganizerStats,
      groupOrganizers,
      uniqueOrganizers,
      uniquePackages,
      pilgrimsByPassport,
      organizers: organizersData // Add organizers data to the return
    };
  } catch (error) {
    console.error('Error fetching operation data:', error);
    throw new Error('Failed to fetch operation data');
  }
};

const processPackageGroups = (
  packages: Record<string, any>, 
  operations: PilgrimOperation[],
  groupIndices: Record<string, string>
): PackageGroup[] => {
  const groups: Record<string, PackageGroup> = {};

  Object.values(packages).forEach((pkg: any) => {
    const groupKey = [
      pkg.mainMakkahHotel, pkg.mainMakkahStartDate, pkg.mainMakkahEndDate,
      pkg.shiftingMakkahHotel || 'none', pkg.shiftingMakkahStartDate || 'none', pkg.shiftingMakkahEndDate || 'none',
      pkg.madinahHotel, pkg.madinahStartDate, pkg.madinahEndDate
    ].join('|');

    if (!groups[groupKey]) {
      groups[groupKey] = {
        id: groupKey,
        name: '',
        customIndex: groupIndices[groupKey] || undefined, // Get custom index if available
        mainMakkah: {
          hotel: pkg.mainMakkahHotel,
          startDate: pkg.mainMakkahStartDate,
          endDate: pkg.mainMakkahEndDate
        },
        shiftingMakkah: pkg.shiftingMakkahHotel ? {
          hotel: pkg.shiftingMakkahHotel,
          startDate: pkg.shiftingMakkahStartDate,
          endDate: pkg.shiftingMakkahEndDate
        } : null,
        madinah: {
          hotel: pkg.madinahHotel,
          startDate: pkg.madinahStartDate,
          endDate: pkg.madinahEndDate
        },
        packages: [],
        pilgrimCount: 0
      };
    }

    groups[groupKey].packages.push(pkg.name);
  });

  Object.values(groups).forEach(group => {
    group.pilgrimCount = operations.filter(p => group.packages.includes(p.packageName)).length;
    group.name = group.packages.join(', ');
  });

  return Object.values(groups).sort((a, b) => b.pilgrimCount - a.pilgrimCount);
};

const calculateStats = (operations: PilgrimOperation[], packageGroups: PackageGroup[]): StatsData => {
  const totalPilgrims = operations.length;
  const maleCount = operations.filter(p => p.gender === 'male').length;
  const femaleCount = operations.filter(p => p.gender === 'female').length;
  const assignedOperators = operations.filter(p => p.operatorId && p.operatorName).length;
  const unassignedOperators = operations.length - assignedOperators;
  const uniqueOrganizers = new Set(operations.map(p => p.organizerId)).size;
  const uniquePackages = new Set(operations.map(p => p.packageName)).size;

  const countryCounts: Record<string, number> = {};
  operations.forEach(p => {
    const country = p.residenceCountry || 'Unknown';
    countryCounts[country] = (countryCounts[country] || 0) + 1;
  });

  const topCountries = Object.entries(countryCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const organizerCounts: Record<string, number> = {};
  operations.forEach(p => {
    organizerCounts[p.organizerName] = (organizerCounts[p.organizerName] || 0) + 1;
  });

  const topOrganizers = Object.entries(organizerCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return {
    totalPilgrims,
    totalPackageGroups: packageGroups.length,
    totalOrganizers: uniqueOrganizers,
    totalPackages: uniquePackages,
    assignedOperators,
    unassignedOperators,
    pilgrimsByGender: {
      male: maleCount,
      female: femaleCount
    },
    topCountries,
    topOrganizers
  };
};

const calculatePackageOrganizerStats = (packageGroups: PackageGroup[], operations: PilgrimOperation[], guidesMap: Record<string, Guide[]>): Record<string, OrganizerPackageStats[]> => {
  const result: Record<string, OrganizerPackageStats[]> = {};

  packageGroups.forEach(group => {
    const groupStats: OrganizerPackageStats[] = [];

    group.packages.forEach(packageName => {
      const packagePilgrims = operations.filter(p => p.packageName === packageName);
      const organizerCounts: Record<string, {id: string, name: string, count: number}> = {};

      packagePilgrims.forEach(pilgrim => {
        const operatorId = pilgrim.operatorId || pilgrim.guideId;
        const operatorOrganizer = getOperatorOrganizer(operatorId, pilgrim, guidesMap);
        const organizerId = operatorOrganizer.id;
        const organizerName = operatorOrganizer.name;

        if (!organizerCounts[organizerId]) {
          organizerCounts[organizerId] = {
            id: organizerId,
            name: organizerName,
            count: 0
          };
        }
        organizerCounts[organizerId].count++;
      });

      const organizers = Object.values(organizerCounts)
        .map(org => ({
          organizerId: org.id,
          organizerName: org.name,
          pilgrimCount: org.count
        }))
        .sort((a, b) => b.pilgrimCount - a.pilgrimCount);

      groupStats.push({
        packageName,
        organizers,
        totalPilgrims: packagePilgrims.length
      });
    });

    result[group.id] = groupStats.sort((a, b) => b.totalPilgrims - a.totalPilgrims);
  });

  return result;
};

const calculateGroupOrganizers = (packageGroups: PackageGroup[], operations: PilgrimOperation[], guidesMap: Record<string, Guide[]>): Record<string, {id: string, name: string}[]> => {
  const result: Record<string, {id: string, name: string}[]> = {};

  packageGroups.forEach(group => {
    const groupPilgrims = operations.filter(p => group.packages.includes(p.packageName));
    const organizersMap = new Map<string, {id: string, name: string}>();

    groupPilgrims.forEach(pilgrim => {
      const operatorId = pilgrim.operatorId || pilgrim.guideId;
      const operatorOrganizer = getOperatorOrganizer(operatorId, pilgrim, guidesMap);

      organizersMap.set(operatorOrganizer.id, {
        id: operatorOrganizer.id,
        name: operatorOrganizer.name
      });
    });

    result[group.id] = Array.from(organizersMap.values());
  });

  return result;
};

// Updated to include the operator organizer information
const getOperatorOrganizer = (operatorId: string, pilgrim: PilgrimOperation, guidesMap: Record<string, Guide[]>): {id: string, name: string} => {
  // Check if we already have the operator organizer information
  if (pilgrim.operatorOrganizerId && pilgrim.operatorOrganizerName) {
    return { 
      id: pilgrim.operatorOrganizerId, 
      name: pilgrim.operatorOrganizerName 
    };
  }

  // Look up the guide in the guides map by operator ID
  const guide = guidesMap[operatorId]?.find(g => g.id === operatorId) || 
                Object.values(guidesMap).flat().find(g => g.id === operatorId);
  
  if (guide && guide.organizerId) {
    const organizerId = guide.organizerId;
    
    // Find the organizer name by looking at other guides with the same organizer
    const anyGuideWithSameOrganizer = Object.values(guidesMap)
      .flat()
      .find(g => g.organizerId === organizerId && g.organizerName);
      
    const organizerName = anyGuideWithSameOrganizer?.organizerName || pilgrim.organizerName || "Unknown";
    
    return { id: organizerId, name: organizerName };
  }

  // Fallback to the pilgrim's organizer
  return { id: pilgrim.organizerId, name: pilgrim.organizerName };
};

export const OperationTakerPage = () => {
  const { translations } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<OperationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrganizer, setSelectedOrganizer] = useState<string>('');
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const [changingOperators, setChangingOperators] = useState<Record<string, boolean>>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState<Record<string, number>>({});
  
  // States for table search
  const [tableSearch, setTableSearch] = useState<Record<string, string>>({});
  
  // States for bulk assignment via Excel
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [bulkAssignments, setBulkAssignments] = useState<BulkAssignment[]>([]);
  const [bulkResults, setBulkResults] = useState<{success: number, error: number, total: number} | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // States for group index renaming
  const [isGroupRenameModalOpen, setIsGroupRenameModalOpen] = useState(false);
  const [groupRenameFile, setGroupRenameFile] = useState<File | null>(null);
  const [isRenamingGroups, setIsRenamingGroups] = useState(false);
  const [groupIndexMappings, setGroupIndexMappings] = useState<GroupIndexMapping[]>([]);
  const [renameResults, setRenameResults] = useState<{success: number, error: number, total: number} | null>(null);
  const groupRenameFileRef = useRef<HTMLInputElement>(null);

  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const operationData = await fetchOperationData();
        setData(operationData);
        setError(null);
      } catch (err) {
        setError((err as Error).message);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const handleRefreshData = async () => {
    setIsRefreshing(true);
    try {
      const operationData = await fetchOperationData();
      setData(operationData);
      toast.success('Data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleExportData = () => {
    if (!data) return;

    try {
      setIsExporting(true);

      const filteredData = data.operations.filter(pilgrim => {
        const matchesSearchTerm = !searchTerm || 
          pilgrim.pilgrimName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pilgrim.passportNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pilgrim.residenceCountry.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pilgrim.organizerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          pilgrim.guideName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesOrganizer = !selectedOrganizer || pilgrim.organizerId === selectedOrganizer;
        const matchesPackage = !selectedPackage || pilgrim.packageName === selectedPackage;

        return matchesSearchTerm && matchesOrganizer && matchesPackage;
      });

      const exportData = filteredData.map(pilgrim => {
        const operatorId = pilgrim.operatorId || pilgrim.guideId;
        const operatorOrganizer = pilgrim.operatorOrganizerId 
          ? { id: pilgrim.operatorOrganizerId, name: pilgrim.operatorOrganizerName || 'Unknown' }
          : getOperatorOrganizer(operatorId, pilgrim, data.organizerGuides);

        // Find the group for the pilgrim's package
        const group = data.packages.find(group => group.packages.includes(pilgrim.packageName));
        const groupIndex = group?.customIndex || data.packages.indexOf(group!) + 1;

        return {
          'Pilgrim Name': pilgrim.pilgrimName,
          'Passport Number': pilgrim.passportNumber,
          'Gender': pilgrim.gender === 'male' ? 'Male' : 'Female',
          'Country': pilgrim.residenceCountry,
          'Package': pilgrim.packageName,
          'Group': `Group ${groupIndex}`,
          'Group Index': groupIndex, 
          'Guide': pilgrim.guideName,
          'Organizer': pilgrim.organizerName,
          'Operator': pilgrim.operatorName || pilgrim.guideName,
          'Operator Organizer': operatorOrganizer.name,
          'Assigned Operator': pilgrim.operatorId ? 'Yes' : 'No',
          'Sync Date': new Date(pilgrim.syncedAt).toLocaleDateString(),
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 30 }, // Pilgrim Name
        { wch: 20 }, // Passport Number
        { wch: 10 }, // Gender
        { wch: 20 }, // Country
        { wch: 25 }, // Package
        { wch: 25 }, // Group
        { wch: 15 }, // Group Index
        { wch: 25 }, // Guide
        { wch: 25 }, // Organizer
        { wch: 25 }, // Operator
        { wch: 25 }, // Operator Organizer
        { wch: 15 }, // Assigned Operator
        { wch: 15 }, // Sync Date
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Operations');

      const date = new Date().toISOString().split('T')[0];
      const filename = `operations-data-${date}.xlsx`;

      XLSX.writeFile(wb, filename);

      toast.success('Data exported successfully');
    } catch (error: any) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  // Updated to include operator organizer information
  const handleChangeOperator = async (pilgrimId: string, newOperatorId: string, newOperatorName: string) => {
    if (userRole !== 'admin') {
      toast.error('Only admin can change operators');
      return;
    }

    setChangingOperators(prev => ({...prev, [pilgrimId]: true}));

    try {
      const operationEntry = data?.operations.find(op => op.pilgrimId === pilgrimId);
      if (!operationEntry) {
        throw new Error('Operation entry not found');
      }

      // Get the operator's organizer information
      const operatorGuide = data?.guides.find(guide => guide.id === newOperatorId);
      if (!operatorGuide) {
        throw new Error('Operator guide not found');
      }

      const operatorOrganizerId = operatorGuide.organizerId;
      
      // Get the organizer's name directly from the organizers collection
      let operatorOrganizerName = "Unknown";
      if (data?.organizers && operatorOrganizerId && data.organizers[operatorOrganizerId]) {
        operatorOrganizerName = data.organizers[operatorOrganizerId].name;
      }

      // Update the operationlist document with new operator and operator organizer information
      await updateDoc(doc(firestore, 'operationlist', operationEntry.id), {
        operatorId: newOperatorId,
        operatorName: newOperatorName,
        operatorOrganizerId: operatorOrganizerId,
        operatorOrganizerName: operatorOrganizerName
      });

      // Update local state
      if (data) {
        const updatedOperations = data.operations.map(op => 
          op.pilgrimId === pilgrimId 
            ? {
                ...op, 
                operatorId: newOperatorId, 
                operatorName: newOperatorName,
                operatorOrganizerId: operatorOrganizerId,
                operatorOrganizerName: operatorOrganizerName
              } 
            : op
        );
        
        setData({
          ...data,
          operations: updatedOperations
        });
      }

      toast.success('Operator updated successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to change operator');
    } finally {
      setChangingOperators(prev => ({...prev, [pilgrimId]: false}));
    }
  };

  // Generate Excel template for bulk assignment
  const handleDownloadTemplate = () => {
    const template = [
      { passport_number: '123456789', operator_guide_name: 'Guide Name Example' },
      { passport_number: '987654321', operator_guide_name: 'Another Guide Name' },
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'bulk-operator-assignment-template.xlsx');
    
    toast.success('Template downloaded');
  };
  
  // Generate Excel template for group index renaming
  const handleDownloadGroupRenameTemplate = () => {
    if (!data) return;
    
    const template = data.packages.map((group, index) => ({
      group_name: group.name,
      current_index: group.customIndex || (index + 1).toString(),
      new_index: ''
    }));

    const ws = XLSX.utils.json_to_sheet(template);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 50 }, // group_name
      { wch: 15 }, // current_index
      { wch: 15 }, // new_index
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Group Indices');
    XLSX.writeFile(wb, 'group-index-rename-template.xlsx');
    
    toast.success('Group index rename template downloaded');
  };

  // Handle bulk upload file selection
  const handleBulkFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setBulkUploadFile(files[0]);
    }
  };
  
  // Handle group rename file selection
  const handleGroupRenameFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setGroupRenameFile(files[0]);
    }
  };

  // Updated to include operator organizer information
  const processBulkUpload = async () => {
    if (!bulkUploadFile || !data) return;
    
    setIsUploading(true);
    setBulkResults(null);
    
    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const fileData = e.target?.result;
          const wb = XLSX.read(fileData, { type: 'binary' });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const assignments: {passport_number: string, operator_guide_name: string}[] = XLSX.utils.sheet_to_json(sheet);
          
          if (!assignments || assignments.length === 0) {
            throw new Error('No valid data found in the file');
          }
          
          // Validate and prepare assignments
          const validatedAssignments: BulkAssignment[] = assignments.map(row => ({
            passportNumber: String(row.passport_number).trim().toLowerCase(),
            operatorName: String(row.operator_guide_name).trim(),
            status: 'pending'
          }));
          
          setBulkAssignments(validatedAssignments);
          
          // Process assignments
          const batch = writeBatch(firestore);
          let successCount = 0;
          let errorCount = 0;
          
          const processedAssignments = await Promise.all(validatedAssignments.map(async (assignment) => {
            const pilgrim = data.pilgrimsByPassport[assignment.passportNumber];
            
            if (!pilgrim) {
              return {
                ...assignment,
                status: 'error' as const,
                message: 'Pilgrim not found with this passport number'
              };
            }
            
            // Find the guide by name
            const guide = data.guides.find(g => 
              g.name.toLowerCase() === assignment.operatorName.toLowerCase()
            );
            
            if (!guide) {
              return {
                ...assignment,
                status: 'error' as const,
                message: 'Guide/Operator not found with this name'
              };
            }
            
            // Get the operator's organizer information
            const operatorOrganizerId = guide.organizerId;
            
            // Get the organizer name directly from the organizers collection
            let operatorOrganizerName = "Unknown";
            if (data.organizers && operatorOrganizerId && data.organizers[operatorOrganizerId]) {
              operatorOrganizerName = data.organizers[operatorOrganizerId].name;
            }
            
            // Update the assignment in Firestore with operator organizer information
            batch.update(doc(firestore, 'operationlist', pilgrim.id), {
              operatorId: guide.id,
              operatorName: guide.name,
              operatorOrganizerId: operatorOrganizerId,
              operatorOrganizerName: operatorOrganizerName
            });
            
            successCount++;
            
            return {
              ...assignment,
              status: 'success' as const,
              message: 'Successfully assigned'
            };
          }));
          
          // Commit the batch
          if (successCount > 0) {
            await batch.commit();
          }
          
          errorCount = processedAssignments.filter(a => a.status === 'error').length;
          
          // Update the local state with results
          setBulkAssignments(processedAssignments);
          setBulkResults({
            success: successCount,
            error: errorCount,
            total: processedAssignments.length
          });
          
          // Update the local data to reflect changes
          if (successCount > 0) {
            const updatedOperations = [...data.operations];
            
            processedAssignments.forEach(assignment => {
              if (assignment.status === 'success') {
                const pilgrimIndex = updatedOperations.findIndex(
                  op => op.passportNumber.toLowerCase() === assignment.passportNumber.toLowerCase()
                );
                
                if (pilgrimIndex !== -1) {
                  const guide = data.guides.find(g => 
                    g.name.toLowerCase() === assignment.operatorName.toLowerCase()
                  );
                  
                  if (guide) {
                    // Get the operator organizer information
                    const operatorOrganizerId = guide.organizerId;
                    let operatorOrganizerName = "Unknown";
                    
                    // Get the organizer name directly from the organizers collection
                    if (data.organizers && operatorOrganizerId && data.organizers[operatorOrganizerId]) {
                      operatorOrganizerName = data.organizers[operatorOrganizerId].name;
                    }
                    
                    updatedOperations[pilgrimIndex] = {
                      ...updatedOperations[pilgrimIndex],
                      operatorId: guide.id,
                      operatorName: guide.name,
                      operatorOrganizerId: operatorOrganizerId,
                      operatorOrganizerName: operatorOrganizerName
                    };
                  }
                }
              }
            });
            
            setData({
              ...data,
              operations: updatedOperations
            });
          }
          
          if (successCount > 0) {
            toast.success(`Successfully assigned ${successCount} pilgrims`);
          }
          
          if (errorCount > 0) {
            toast.error(`Failed to assign ${errorCount} pilgrims`);
          }
          
        } catch (error) {
          console.error('Error processing file:', error);
          toast.error('Error processing file: ' + (error as Error).message);
        } finally {
          setIsUploading(false);
        }
      };
      
      reader.onerror = () => {
        toast.error('Error reading file');
        setIsUploading(false);
      };
      
      reader.readAsBinaryString(bulkUploadFile);
      
    } catch (error) {
      console.error('Error in bulk upload:', error);
      toast.error('An error occurred during bulk upload');
      setIsUploading(false);
    }
  };
  
  // Process the group index file
  const processGroupIndexRename = async () => {
    if (!groupRenameFile || !data) return;
    
    setIsRenamingGroups(true);
    setRenameResults(null);
    
    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const fileData = e.target?.result;
          const wb = XLSX.read(fileData, { type: 'binary' });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const groupMappings: {group_name: string, current_index: string, new_index: string}[] = XLSX.utils.sheet_to_json(sheet);
          
          if (!groupMappings || groupMappings.length === 0) {
            throw new Error('No valid data found in the file');
          }
          
          // Validate and prepare mappings
          const validatedMappings: GroupIndexMapping[] = groupMappings
            .filter(row => row.new_index?.toString().trim()) // Only process rows with new indices
            .map(row => ({
              groupName: row.group_name,
              oldIndex: row.current_index.toString(),
              newIndex: row.new_index.toString().trim(),
              status: 'pending'
            }));
          
          setGroupIndexMappings(validatedMappings);
          
          if (validatedMappings.length === 0) {
            throw new Error('No new group indices provided. Please fill in at least one "new_index" field.');
          }

          // Get existing indices
          const referenceDocRef = doc(firestore, 'packageGroupIndices', 'reference');
          const referenceDoc = await getDoc(referenceDocRef);
          const existingIndices = referenceDoc.exists() ? referenceDoc.data() : {};
          
          // Prepare updates
          const newIndices = { ...existingIndices };
          let successCount = 0;
          let errorCount = 0;
          
          // Process each mapping
          const processedMappings = validatedMappings.map(mapping => {
            try {
              // Find the group with this name
              const group = data.packages.find(g => g.name === mapping.groupName);
              
              if (!group) {
                return {
                  ...mapping,
                  status: 'error' as const,
                  message: 'Group not found'
                };
              }

              // Add the group to our indices map with the new index
              newIndices[group.id] = mapping.newIndex;
              successCount++;
              
              return {
                ...mapping,
                status: 'success' as const,
                message: 'Successfully updated'
              };
            } catch (err) {
              errorCount++;
              return {
                ...mapping,
                status: 'error' as const,
                message: (err as Error).message
              };
            }
          });

          // Update the single reference document with all indices
          await setDoc(referenceDocRef, newIndices);
          
          // Update the local state with results
          setGroupIndexMappings(processedMappings);
          setRenameResults({
            success: successCount,
            error: errorCount,
            total: processedMappings.length
          });
          
          // Update the local data to reflect changes
          if (successCount > 0) {
            const updatedPackages = [...data.packages];
            
            processedMappings.forEach(mapping => {
              if (mapping.status === 'success') {
                const groupIndex = updatedPackages.findIndex(g => g.name === mapping.groupName);
                if (groupIndex !== -1) {
                  updatedPackages[groupIndex] = {
                    ...updatedPackages[groupIndex],
                    customIndex: mapping.newIndex
                  };
                }
              }
            });
            
            setData({
              ...data,
              packages: updatedPackages
            });
          }
          
          if (successCount > 0) {
            toast.success(`Successfully updated ${successCount} group indices`);
          }
          
          if (errorCount > 0) {
            toast.error(`Failed to update ${errorCount} group indices`);
          }
          
        } catch (error) {
          console.error('Error processing file:', error);
          toast.error('Error processing file: ' + (error as Error).message);
        } finally {
          setIsRenamingGroups(false);
        }
      };
      
      reader.onerror = () => {
        toast.error('Error reading file');
        setIsRenamingGroups(false);
      };
      
      reader.readAsBinaryString(groupRenameFile);
      
    } catch (error) {
      console.error('Error in group index update:', error);
      toast.error('An error occurred during group index update');
      setIsRenamingGroups(false);
    }
  };

  const getFilteredPilgrims = (groupPackages: string[], groupId: string) => {
    if (!data) return [];

    return data.operations.filter(pilgrim => {
      // Base filters (global)
      const matchesGlobalSearch = !searchTerm || 
        pilgrim.pilgrimName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pilgrim.passportNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pilgrim.residenceCountry.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pilgrim.organizerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pilgrim.guideName.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesOrganizer = !selectedOrganizer || pilgrim.organizerId === selectedOrganizer;
      const matchesPackage = groupPackages.includes(pilgrim.packageName) && 
                             (!selectedPackage || pilgrim.packageName === selectedPackage);

      // Table-specific search (if any)
      const tableSearchTerm = tableSearch[groupId] || '';
      const matchesTableSearch = !tableSearchTerm || 
        pilgrim.pilgrimName.toLowerCase().includes(tableSearchTerm.toLowerCase()) ||
        pilgrim.passportNumber.toLowerCase().includes(tableSearchTerm.toLowerCase()) ||
        pilgrim.residenceCountry.toLowerCase().includes(tableSearchTerm.toLowerCase()) ||
        pilgrim.organizerName.toLowerCase().includes(tableSearchTerm.toLowerCase()) ||
        pilgrim.guideName.toLowerCase().includes(tableSearchTerm.toLowerCase());

      return matchesGlobalSearch && matchesOrganizer && matchesPackage && matchesTableSearch;
    });
  };

  const getGuidesForOrganizer = (organizerId: string) => {
    if (!data) return [];

    const orgGuides = data.organizerGuides[organizerId] || [];
    if (orgGuides.length > 0) {
      return orgGuides;
    }

    return data.guides.filter(guide => guide.organizerId === organizerId);
  };

  const shouldMaskPilgrimData = (pilgrim: PilgrimOperation) => {
    if (userRole === 'admin') return false;
    if (userRole === 'organizer' && pilgrim.organizerId === userId) return false;
    if (userRole === 'guide' && pilgrim.guideId === userId) return false;
    return true;
  };

  const maskData = (data: string) => {
    return '****';
  };

  const handleTableSearch = (groupId: string, term: string) => {
    setTableSearch(prev => ({
      ...prev,
      [groupId]: term
    }));
    
    // Reset to first page when searching
    setCurrentPage(prev => ({
      ...prev,
      [groupId]: 1
    }));
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="text-red-500">Error: {error}</div>;
  }

  if (!data) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Radio className="text-primary-500" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {translations?.operationTaker?.title || 'Operation Management'}
            </h1>
            <p className="text-gray-500 mt-1">
              {translations?.operationTaker?.subtitle || 'Assign operators to accommodation groups'}
            </p>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3">
          {/* Rename Group Indices Button - Only for admin */}
          {userRole === 'admin' && (
            <button 
              onClick={() => setIsGroupRenameModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all shadow-sm"
            >
              <Edit size={18} />
              <span>Rename Group Numbers</span>
            </button>
          )}
        
          {/* Bulk Assign Button - Only for admin */}
          {userRole === 'admin' && (
            <button 
              onClick={() => setIsBulkModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all shadow-sm"
            >
              <Upload size={18} />
              <span>Bulk Assign</span>
            </button>
          )}
          
          {/* Export button for all users */}
          <button 
            onClick={handleExportData}
            disabled={isExporting}
            className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-all shadow-sm"
          >
            <Download size={18} className={isExporting ? 'animate-spin' : ''} />
            <span>{isExporting ? 'Exporting...' : 'Export Data'}</span>
          </button>
          
          {/* Refresh button for admin */}
          {userRole === 'admin' && (
            <button 
              onClick={handleRefreshData}
              disabled={isRefreshing}
              className="flex items-center gap-2 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-all shadow-sm"
            >
              <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
              <span>{isRefreshing ? 'Refreshing...' : 'Refresh Data'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Statistics Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Pilgrims */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Users className="text-primary-500" size={20} />
            </div>
            <h3 className="font-medium text-gray-700">Total Pilgrims</h3>
          </div>
          <div className="flex items-end justify-between">
            <div className="text-3xl font-bold text-gray-900">{data.operations.length}</div>
            <div className="flex flex-col items-end">
              <span className="text-sm text-gray-500">{data.stats.pilgrimsByGender.male} male</span>
              <span className="text-sm text-gray-500">{data.stats.pilgrimsByGender.female} female</span>
            </div>
          </div>
          <div className="mt-4 bg-gray-100 h-2 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-500" 
              style={{ 
                width: `${data.operations.length ? data.stats.pilgrimsByGender.male / data.operations.length * 100 : 0}%` 
              }}
            ></div>
          </div>
        </motion.div>

        {/* Accommodation Groups */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-50 rounded-lg">
              <Package className="text-green-500" size={20} />
            </div>
            <h3 className="font-medium text-gray-700">Accommodation Groups</h3>
          </div>
          <div className="flex items-end justify-between">
            <div className="text-3xl font-bold text-gray-900">{data.packages.length}</div>
            <div className="flex flex-col items-end">
              <span className="text-sm text-gray-500">{data.uniquePackages.length} packages</span>
              <span className="text-sm text-gray-500">{data.uniqueOrganizers.length} organizers</span>
            </div>
          </div>
        </motion.div>

        {/* Operator Assignment */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-50 rounded-lg">
              <UserCog className="text-purple-500" size={20} />
            </div>
            <h3 className="font-medium text-gray-700">Operator Assignment</h3>
          </div>
          <div className="flex items-end justify-between">
            <div className="text-3xl font-bold text-gray-900">
              {((data.stats.assignedOperators / (data.operations.length || 1)) * 100).toFixed(0)}%
            </div>
            <div className="flex flex-col items-end">
              <span className="text-sm text-green-500">{data.stats.assignedOperators} assigned</span>
              <span className="text-sm text-gray-500">{data.stats.unassignedOperators} default</span>
            </div>
          </div>
          <div className="mt-4 bg-gray-100 h-2 rounded-full overflow-hidden">
            <div 
              className="h-full bg-purple-500" 
              style={{ 
                width: `${data.operations.length ? data.stats.assignedOperators / data.operations.length * 100 : 0}%` 
              }}
            ></div>
          </div>
        </motion.div>

        
        {/* Top Countries */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <BarChart3 className="text-yellow-500" size={20} />
            </div>
            <h3 className="font-medium text-gray-700">Top Countries</h3>
          </div>
          <div className="space-y-2">
            {data.stats.topCountries.slice(0, 3).map((country, index) => (
              <div key={country.name} className="flex items-center justify-between">
                <span className="text-sm font-medium">{country.name}</span>
                <div className="flex items-center gap-2">
                  <div className="w-20 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-400" 
                      style={{ 
                        width: `${data.operations.length ? country.count / data.operations.length * 100 : 0}%` 
                      }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-500">{country.count}</span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder={translations?.operationTaker?.searchPlaceholder || 'Search by name, passport, country...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          </div>

          {/* Organizer Filter */}
          <select
            value={selectedOrganizer}
            onChange={(e) => setSelectedOrganizer(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">
              {translations?.operationTaker?.allOrganizers || 'All Organizers'}
            </option>
            {data.uniqueOrganizers.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>

          {/* Package Filter */}
          <select
            value={selectedPackage}
            onChange={(e) => setSelectedPackage(e.target.value)}
            className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">
              {translations?.operationTaker?.allPackages || 'All Packages'}
            </option>
            {data.uniquePackages.map((packageName) => (
              <option key={packageName} value={packageName}>
                {packageName}
              </option>
            ))}
          </select>
        </div>

        {/* Active Filters */}
        {(searchTerm || selectedOrganizer || selectedPackage) && (
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-primary-500" />
            <span className="text-sm text-gray-600">
              {translations?.operationTaker?.activeFilters || 'Active Filters:'}
            </span>
            <div className="flex flex-wrap gap-2">
              {searchTerm && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {translations?.operationTaker?.search || 'Search'}: {searchTerm}
                </span>
              )}
              {selectedOrganizer && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {translations?.operationTaker?.organizer || 'Organizer'}: {
                    data.uniqueOrganizers.find(org => org.id === selectedOrganizer)?.name || selectedOrganizer
                  }
                </span>
              )}
              {selectedPackage && (
                <span className="text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded">
                  {translations?.operationTaker?.package || 'Package'}: {selectedPackage}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Accommodation Groups */}
      <div className="space-y-6">
        {data.packages.map((group, origIndex) => {
          const groupPilgrims = getFilteredPilgrims(group.packages, group.id);
          
          if (groupPilgrims.length === 0) {
            return null;
          }
          
          let groupOrganizersList = data.groupOrganizers[group.id] || [];
          
          if (groupOrganizersList.length === 0) {
            const pilgrimsInGroup = data.operations.filter(p => group.packages.includes(p.packageName));
            const organizersInGroup = [...new Set(pilgrimsInGroup.map(p => p.organizerId))];
            
            const manualOrganizers = organizersInGroup.map(orgId => {
              const pilgrim = pilgrimsInGroup.find(p => p.organizerId === orgId);
              return {
                id: orgId,
                name: pilgrim?.organizerName || 'Unknown'
              };
            });
            
            groupOrganizersList = manualOrganizers;
          }
          
          const groupStats = data.packageOrganizerStats[group.id] || [];

          if (groupStats.length === 0 && groupPilgrims.length > 0) {
            const onDemandStats = calculatePackageOrganizerStats(
              [group], 
              groupPilgrims, 
              data.organizerGuides
            );
            
            data.packageOrganizerStats[group.id] = onDemandStats[group.id];
          }

          const currentGroupPage = currentPage[group.id] || 1;
          const totalPages = Math.ceil(groupPilgrims.length / ITEMS_PER_PAGE);
          const startIndex = (currentGroupPage - 1) * ITEMS_PER_PAGE;
          const paginatedPilgrims = groupPilgrims.slice(startIndex, startIndex + ITEMS_PER_PAGE);
          
          // Calculate organizer statistics for this specific group
          const organizersInGroup: Record<string, { id: string, name: string, count: number }> = {};
          groupPilgrims.forEach(pilgrim => {
            if (!organizersInGroup[pilgrim.organizerId]) {
              organizersInGroup[pilgrim.organizerId] = {
                id: pilgrim.organizerId,
                name: pilgrim.organizerName,
                count: 0
              };
            }
            organizersInGroup[pilgrim.organizerId].count++;
          });
          
          const groupOrganizerStats = Object.values(organizersInGroup)
            .sort((a, b) => b.count - a.count);
          
          // Use the custom index if available, otherwise use the original index + 1
          const groupIndex = group.customIndex || (origIndex + 1).toString();
          
          return (
            <motion.div
              key={group.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: origIndex * 0.05 }}
              className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow"
            >
              {/* Group Header */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 transition-colors border-b"
                onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-50 rounded-lg">
                      <Package className="text-primary-500" size={20} />
                    </div>
                    <div>
                      <h3 className="font-medium text-lg">
                        {translations?.operationTaker?.accommodationGroup || 'Group'} {groupIndex}:{' '}
                        <span className="text-primary-600">{group.name}</span>
                      </h3>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <Building2 size={14} className="text-gray-400" />
                          <span>
                            {group.mainMakkah.hotel} → {group.madinah.hotel}
                            {group.shiftingMakkah && ` → ${group.shiftingMakkah.hotel}`}
                          </span>
                        </div>
                        <div className="px-2 py-0.5 bg-primary-50 text-primary-600 rounded-full text-xs font-medium">
                          {groupPilgrims.length} pilgrims
                        </div>
                        <div className="px-2 py-0.5 bg-gray-50 text-gray-600 rounded-full text-xs">
                          {group.packages.length} packages
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {expandedGroup === group.id ? (
                      <ChevronUp size={20} className="text-gray-400" />
                    ) : (
                      <ChevronDown size={20} className="text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              <AnimatePresence>
                {expandedGroup === group.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    {/* Group Stats */}
                    <div className="p-4 border-b bg-gray-50">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Main Makkah */}
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <h4 className="font-medium flex items-center gap-2 text-red-600 mb-2">
                            <Building2 size={16} />
                            {translations?.operationTaker?.mainMakkah || 'Main Makkah'}
                          </h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-500">
                                {translations?.operationTaker?.hotel || 'Hotel'}:
                              </span>
                              <p className="font-medium text-gray-800">{group.mainMakkah.hotel}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                {translations?.operationTaker?.date || 'Date'}:
                              </span>
                              <p className="font-medium text-gray-800">
                                {group.mainMakkah.startDate} - {group.mainMakkah.endDate.split('/').slice(-1)[0]}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Shifting Makkah (if available) */}
                        {group.shiftingMakkah && (
                          <div className="bg-white p-3 rounded-lg shadow-sm">
                            <h4 className="font-medium flex items-center gap-2 text-blue-600 mb-2">
                              <Building2 size={16} />
                              {translations?.operationTaker?.shiftingMakkah || 'Shifting Makkah'}
                            </h4>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                              <div>
                                <span className="text-gray-500">
                                  {translations?.operationTaker?.hotel || 'Hotel'}:
                                </span>
                                <p className="font-medium text-gray-800">{group.shiftingMakkah.hotel}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">
                                  {translations?.operationTaker?.date || 'Date'}:
                                </span>
                                <p className="font-medium text-gray-800">
                                  {group.shiftingMakkah.startDate} - {group.shiftingMakkah.endDate.split('/').slice(-1)[0]}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Madinah */}
                        <div className="bg-white p-3 rounded-lg shadow-sm">
                          <h4 className="font-medium flex items-center gap-2 text-green-600 mb-2">
                            <Building2 size={16} />
                            {translations?.operationTaker?.madinah || 'Madinah'}
                          </h4>
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-500">
                                {translations?.operationTaker?.hotel || 'Hotel'}:
                              </span>
                              <p className="font-medium text-gray-800">{group.madinah.hotel}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">
                                {translations?.operationTaker?.date || 'Date'}:
                              </span>
                              <p className="font-medium text-gray-800">
                                {group.madinah.startDate} - {group.madinah.endDate.split('/').slice(-1)[0]}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Organizer Statistics for this Group */}
                    <div className="p-4 border-b bg-white">
                      <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <Building2 size={16} className="text-primary-500" />
                        {translations?.operationTaker?.groupOrganizerStats || 'Organizer Statistics for this Group'}
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {groupOrganizerStats.map(org => (
                          <div key={org.id} className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex justify-between items-center mb-2">
                              <h5 className="font-medium">{org.name}</h5>
                              <span className="text-primary-600 font-semibold">{org.count}</span>
                            </div>
                            <div className="w-full bg-gray-200 h-2 rounded-full">
                              <div 
                                className="bg-primary-500 h-2 rounded-full"
                                style={{ width: `${(org.count / groupPilgrims.length * 100)}%` }}
                              />
                            </div>
                            <div className="text-right text-xs text-gray-500 mt-1">
                              {Math.round(org.count / groupPilgrims.length * 100)}% of pilgrims
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Package-Organizer Statistics */}
                    <div className="p-4 border-b bg-white">
                      <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <PieChart size={16} className="text-primary-500" />
                        {translations?.operationTaker?.packageStats || 'Package Statistics by Organizer'}
                      </h4>
                      
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {translations?.operationTaker?.package || 'Package'}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {translations?.operationTaker?.organizer || 'Organizer'}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {translations?.operationTaker?.pilgrimCount || 'Pilgrim Count'}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {(() => {
                              const stats = data.packageOrganizerStats[group.id];
                              
                              if (!stats || stats.length === 0 || stats.every(pkg => pkg.totalPilgrims === 0)) {
                                const directPackageStats: Record<string, {
                                  organizers: Record<string, {count: number, name: string}>;
                                  total: number;
                                }> = {};
                                
                                const pilgrimsInGroup = data.operations.filter(p => group.packages.includes(p.packageName));
                                
                                pilgrimsInGroup.forEach(pilgrim => {
                                  if (!directPackageStats[pilgrim.packageName]) {
                                    directPackageStats[pilgrim.packageName] = {
                                      organizers: {},
                                      total: 0
                                    };
                                  }
                                  
                                  if (!directPackageStats[pilgrim.packageName].organizers[pilgrim.organizerId]) {
                                    directPackageStats[pilgrim.packageName].organizers[pilgrim.organizerId] = {
                                      count: 0,
                                      name: pilgrim.organizerName
                                    };
                                  }
                                  
                                  directPackageStats[pilgrim.packageName].organizers[pilgrim.organizerId].count++;
                                  directPackageStats[pilgrim.packageName].total++;
                                });
                                
                                const pkgStats = Object.entries(directPackageStats).map(([pkgName, data]) => {
                                  const organizerList = Object.entries(data.organizers).map(([orgId, orgData]) => ({
                                    organizerId: orgId,
                                    organizerName: orgData.name,
                                    pilgrimCount: orgData.count
                                  })).sort((a, b) => b.pilgrimCount - a.pilgrimCount);
                                  
                                  return {
                                    packageName: pkgName,
                                    organizers: organizerList,
                                    totalPilgrims: data.total
                                  };
                                }).sort((a, b) => b.totalPilgrims - a.totalPilgrims);
                                
                                return pkgStats.flatMap(packageStat => 
                                  packageStat.organizers.length > 0 ? (
                                    packageStat.organizers.map((orgStat, orgIndex) => (
                                      <tr 
                                        key={`${packageStat.packageName}-${orgStat.organizerId}`} 
                                        className={orgIndex === 0 ? 'bg-gray-50' : ''}
                                      >
                                        {orgIndex === 0 ? (
                                          <td 
                                            rowSpan={packageStat.organizers.length} 
                                            className="px-4 py-3 align-top font-medium text-gray-900 border-r"
                                          >
                                            <div className="flex flex-col">
                                              <span>{packageStat.packageName}</span>
                                              <span className="mt-1 text-xs text-primary-600">
                                                {packageStat.totalPilgrims} total pilgrims
                                              </span>
                                            </div>
                                          </td>
                                        ) : null}
                                        <td className="px-4 py-2 text-gray-800">
                                          {orgStat.organizerName}
                                        </td>
                                        <td className="px-4 py-2">
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-700">
                                              {orgStat.pilgrimCount}
                                            </span>
                                            <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                                              <div 
                                                className="h-full bg-primary-400" 
                                                style={{ 
                                                  width: `${packageStat.totalPilgrims ? orgStat.pilgrimCount / packageStat.totalPilgrims * 100 : 0}%` 
                                                }}
                                              />
                                            </div>
                                            <span className="text-xs text-gray-500">
                                              ({Math.round(orgStat.pilgrimCount / packageStat.totalPilgrims * 100)}%)
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr key={packageStat.packageName}>
                                      <td className="px-4 py-3 font-medium text-gray-900">
                                        {packageStat.packageName}
                                        <span className="ml-2 text-xs text-primary-600">
                                          ({packageStat.totalPilgrims} pilgrims)
                                        </span>
                                      </td>
                                      <td colSpan={2} className="px-4 py-3 text-center text-gray-500">
                                        {translations?.operationTaker?.noOrganizers || 'No organizers found'}
                                      </td>
                                    </tr>
                                  )
                                );
                              } else {
                                return stats.flatMap(packageStat => 
                                  packageStat.organizers.length > 0 ? (
                                    packageStat.organizers.map((orgStat, orgIndex) => (
                                      <tr 
                                        key={`${packageStat.packageName}-${orgStat.organizerId}`} 
                                        className={orgIndex === 0 ? 'bg-gray-50' : ''}
                                      >
                                        {orgIndex === 0 ? (
                                          <td 
                                            rowSpan={packageStat.organizers.length} 
                                            className="px-4 py-3 align-top font-medium text-gray-900 border-r"
                                          >
                                            <div className="flex flex-col">
                                              <span>{packageStat.packageName}</span>
                                              <span className="mt-1 text-xs text-primary-600">
                                                {packageStat.totalPilgrims} total pilgrims
                                              </span>
                                            </div>
                                          </td>
                                        ) : null}
                                        <td className="px-4 py-2 text-gray-800">
                                          {orgStat.organizerName}
                                        </td>
                                        <td className="px-4 py-2">
                                          <div className="flex items-center gap-2">
                                            <span className="font-medium text-gray-700">
                                              {orgStat.pilgrimCount}
                                            </span>
                                            <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                                              <div 
                                                className="h-full bg-primary-400" 
                                                style={{ 
                                                  width: `${packageStat.totalPilgrims ? orgStat.pilgrimCount / packageStat.totalPilgrims * 100 : 0}%` 
                                                }}
                                              />
                                            </div>
                                            <span className="text-xs text-gray-500">
                                              ({Math.round(orgStat.pilgrimCount / packageStat.totalPilgrims * 100)}%)
                                            </span>
                                          </div>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr key={packageStat.packageName}>
                                      <td className="px-4 py-3 font-medium text-gray-900">
                                        {packageStat.packageName}
                                        <span className="ml-2 text-xs text-primary-600">
                                          ({packageStat.totalPilgrims} pilgrims)
                                        </span>
                                      </td>
                                      <td colSpan={2} className="px-4 py-3 text-center text-gray-500">
                                        {translations?.operationTaker?.noOrganizers || 'No organizers found'}
                                      </td>
                                    </tr>
                                  )
                                );
                              }
                            })()}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    {/* Table Search - Added for in-table search */}
                    <div className="p-4 bg-gray-50 border-b">
                      <div className="relative max-w-md">
                        <input
                          type="text"
                          placeholder="Search in this group..."
                          value={tableSearch[group.id] || ''}
                          onChange={(e) => handleTableSearch(group.id, e.target.value)}
                          className="w-full pl-10 pr-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
                        />
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                      </div>
                    </div>
                    
                    {/* Pilgrims Table */}
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {translations?.operationTaker?.pilgrim || 'Pilgrim'}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {translations?.operationTaker?.package || 'Package'}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {translations?.operationTaker?.organizer || 'Organizer'}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {translations?.operationTaker?.guide || 'Guide'}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {translations?.operationTaker?.operator || 'Operator'}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {paginatedPilgrims.map((pilgrim) => {
                            const isChanging = changingOperators[pilgrim.pilgrimId];
                            const shouldMask = shouldMaskPilgrimData(pilgrim);
                            
                            return (
                              <tr key={pilgrim.id} className="hover:bg-gray-50">
                                {/* Pilgrim Info */}
                                <td className="px-6 py-4">
                                  <div className="flex items-center">
                                    <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                                      pilgrim.gender === 'male' ? 'bg-blue-100' : 'bg-pink-100'
                                    }`}>
                                      <User className={pilgrim.gender === 'male' ? 'text-blue-500' : 'text-pink-500'} size={20} />
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium text-gray-900">
                                        {shouldMask ? maskData(pilgrim.pilgrimName) : pilgrim.pilgrimName}
                                      </div>
                                      <div className="text-sm text-gray-500 flex items-center gap-1">
                                        <span>{shouldMask ? maskData(pilgrim.passportNumber) : pilgrim.passportNumber}</span>
                                        <span className="text-gray-300">|</span>
                                        <MapPin size={12} className="inline" />
                                        <span>{pilgrim.residenceCountry}</span>
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                {/* Package */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 font-medium">{pilgrim.packageName}</div>
                                </td>

                                {/* Organizer */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">{pilgrim.organizerName}</div>
                                </td>

                                {/* Guide */}
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900">{pilgrim.guideName}</div>
                                </td>

                                {/* Operator Selection */}
                                <td className="px-6 py-4">
                                  {userRole === 'admin' ? (
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <div className={`text-sm font-medium ${
                                          pilgrim.operatorId ? 'text-green-600' : 'text-gray-600'
                                        }`}>
                                          {pilgrim.operatorName || pilgrim.guideName}
                                          {pilgrim.operatorId && (
                                            <span className="ml-2 px-1.5 py-0.5 bg-green-50 text-green-600 text-xs rounded-full">
                                              assigned
                                            </span>
                                          )}
                                        </div>
                                        {isChanging && (
                                          <Loader2 size={16} className="animate-spin text-primary-500" />
                                        )}
                                      </div>
                                      
                                      {/* Display operator organizer name */}
                                      {pilgrim.operatorOrganizerId && pilgrim.operatorOrganizerName && (
                                        <div className="text-xs text-gray-500">
                                          {pilgrim.operatorOrganizerName}
                                        </div>
                                      )}
                                      
                                      {/* Operator Dropdown */}
                                      <div className="mt-1">
                                        <select
                                          disabled={isChanging}
                                          className="w-full px-3 py-2 text-sm bg-white border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                                          value={`${pilgrim.organizerId}|${pilgrim.operatorId || pilgrim.guideId}`}
                                          onChange={(e) => {
                                            const [orgId, guideId] = e.target.value.split('|');
                                            
                                            let selectedGuide;
                                            
                                            if (data.organizerGuides[orgId]?.length > 0) {
                                              selectedGuide = data.organizerGuides[orgId].find(g => g.id === guideId);
                                            }
                                            
                                            if (!selectedGuide) {
                                              selectedGuide = data.guides.find(g => g.id === guideId && g.organizerId === orgId);
                                            }
                                            
                                            if (selectedGuide) {
                                              handleChangeOperator(
                                                pilgrim.pilgrimId, 
                                                selectedGuide.id, 
                                                selectedGuide.name
                                              );
                                            } else {
                                              toast.error("Selected guide not found");
                                            }
                                          }}
                                        >
                                          {/* Default option - current guide */}
                                          <option value={`${pilgrim.organizerId}|${pilgrim.operatorId || pilgrim.guideId}`}>
                                            {pilgrim.operatorName || pilgrim.guideName} (Current)
                                          </option>
                                          
                                          {/* Group options by organizer */}
                                          {groupOrganizersList.length > 0 ? (
                                            groupOrganizersList.map((org) => {
                                              const guides = getGuidesForOrganizer(org.id);
                                              
                                              if (guides.length === 0) {
                                                return null;
                                              }
                                              
                                              return (
                                                <optgroup key={org.id} label={`${org.name}`}>
                                                  {guides.map(guide => {
                                                    if (guide.id === (pilgrim.operatorId || pilgrim.guideId)) {
                                                      return null;
                                                    }
                                                    
                                                    return (
                                                      <option 
                                                        key={`${org.id}|${guide.id}`} 
                                                        value={`${org.id}|${guide.id}`}
                                                      >
                                                        {guide.name}
                                                      </option>
                                                    );
                                                  })}
                                                </optgroup>
                                              );
                                            }).filter(Boolean)
                                          ) : (
                                            <option disabled>No guides available</option>
                                          )}
                                        </select>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex flex-col">
                                      <div className={`text-sm ${
                                        pilgrim.operatorId ? 'font-medium text-green-600' : 'text-gray-900'
                                      }`}>
                                        {pilgrim.operatorName || pilgrim.guideName}
                                        {pilgrim.operatorId && (
                                          <span className="ml-2 px-1.5 py-0.5 bg-green-50 text-green-600 text-xs rounded-full">
                                            assigned
                                          </span>
                                        )}
                                      </div>
                                      
                                      {/* Display operator organizer name */}
                                      {pilgrim.operatorOrganizerId && pilgrim.operatorOrganizerName && (
                                        <div className="text-xs text-gray-500 mt-1">
                                          {pilgrim.operatorOrganizerName}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {groupPilgrims.length > ITEMS_PER_PAGE && (
                      <div className="px-4 py-3 flex items-center justify-between border-t">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setCurrentPage({
                              ...currentPage,
                              [group.id]: Math.max(1, (currentPage[group.id] || 1) - 1)
                            })}
                            disabled={currentGroupPage === 1}
                            className={`p-1 rounded-lg ${
                              currentGroupPage === 1
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <ChevronRight size={20} />
                          </button>

                          <span className="text-sm text-gray-700">
                            {translations?.pagination?.page || 'Page'} {currentGroupPage}{' '}
                            {translations?.pagination?.of || 'of'} {totalPages}
                          </span>

                          <button
                            onClick={() => setCurrentPage({
                              ...currentPage,
                              [group.id]: Math.min(totalPages, (currentPage[group.id] || 1) + 1)
                            })}
                            disabled={currentGroupPage === totalPages}
                            className={`p-1 rounded-lg ${
                              currentGroupPage === totalPages
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <ChevronLeft size={20} />
                          </button>
                        </div>

                        <div className="text-sm text-gray-500">
                          {translations?.pagination?.showing || 'Showing'} {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, groupPilgrims.length)} {translations?.pagination?.of || 'of'} {groupPilgrims.length}
                        </div>
                      </div>
                    )}

                    {groupPilgrims.length === 0 && (
                      <div className="text-center py-8">
                        <Users size={48} className="mx-auto text-gray-300 mb-4" />
                        <p className="text-gray-500">
                          {translations?.operationTaker?.noPilgrims || 'No pilgrims found in this group'}
                        </p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}

        {data.packages.filter(group => getFilteredPilgrims(group.packages, group.id).length > 0).length === 0 && (
          <div className="bg-white p-8 rounded-lg shadow-sm text-center">
            <Package size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">
              {searchTerm || selectedOrganizer || selectedPackage
                ? translations?.operationTaker?.noFilteredGroups || 'No accommodation groups match your filters'
                : translations?.operationTaker?.noGroups || 'No accommodation groups found'}
            </p>
          </div>
        )}
      </div>

      {/* Bulk Assignment Modal */}
      <Modal
        isOpen={isBulkModalOpen}
        onClose={() => {
          setIsBulkModalOpen(false);
          setBulkUploadFile(null);
          setBulkAssignments([]);
          setBulkResults(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
        title="Bulk Assign Operators via Excel"
        width="90vw"
        maxWidth="700px"
      >
        <div className="space-y-6 p-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex gap-3 items-start">
              <AlertCircle className="text-blue-600 mt-0.5 flex-shrink-0" size={18} />
              <div>
                <p className="text-blue-700 font-medium">Instructions</p>
                <p className="text-blue-600 text-sm mt-1">
                  Upload an Excel file with two columns: <code>passport_number</code> and <code>operator_guide_name</code>.
                  The system will assign the specified guide as the operator for each matching pilgrim.
                </p>
                <ul className="list-disc list-inside text-sm text-blue-600 mt-2">
                  <li>The passport number must match exactly</li>
                  <li>The guide name must match exactly as in the system</li>
                  <li>Changes take effect immediately for all matching pilgrims</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Template Download */}
          <button
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
          >
            <Download size={18} />
            <span>Download Excel Template</span>
          </button>

          {/* File Upload */}
          {!bulkResults && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleBulkFileChange}
                accept=".xlsx,.xls"
                className="hidden"
              />
              <div className="space-y-2">
                {bulkUploadFile ? (
                  <div>
                    <div className="flex items-center justify-center mb-3">
                      <div className="p-2 bg-green-100 rounded-full">
                        <Check className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                    <p className="text-green-600 font-medium">{bulkUploadFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(bulkUploadFile.size / 1024).toFixed(2)} KB
                    </p>
                    <div className="mt-2">
                      <button
                        onClick={() => {
                          setBulkUploadFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="text-red-500 text-sm hover:text-red-700"
                      >
                        Remove File
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2">
                      <button
                        type="button"
                        className="font-medium text-primary-600 hover:text-primary-500"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Select a file
                      </button>{' '}
                      or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Excel files only</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Results Table */}
          {bulkAssignments.length > 0 && (
            <div className="mt-4 border rounded-lg overflow-hidden">
              <div className="max-h-80 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Passport
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Operator Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {bulkAssignments.map((assignment, index) => (
                      <tr key={index} className={assignment.status === 'error' ? 'bg-red-50' : (assignment.status === 'success' ? 'bg-green-50' : '')}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {assignment.passportNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {assignment.operatorName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {assignment.status === 'pending' ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              Pending
                            </span>
                          ) : assignment.status === 'success' ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Success
                            </span>
                          ) : (
                            <span title={assignment.message} className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              Error: {assignment.message}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Summary Results */}
          {bulkResults && (
            <div className={`p-4 rounded-lg ${
              bulkResults.error > 0 ? 'bg-yellow-50' : 'bg-green-50'
            }`}>
              <h4 className={`font-medium ${
                bulkResults.error > 0 ? 'text-yellow-700' : 'text-green-700'
              }`}>
                Assignment Complete
              </h4>
              <div className="mt-2 space-y-1">
                <p className="text-sm">
                  <span className="font-medium">Total:</span> {bulkResults.total} assignments
                </p>
                <p className="text-sm text-green-700">
                  <span className="font-medium">Success:</span> {bulkResults.success} assignments
                </p>
                {bulkResults.error > 0 && (
                  <p className="text-sm text-red-700">
                    <span className="font-medium">Failed:</span> {bulkResults.error} assignments
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => {
                setIsBulkModalOpen(false);
                setBulkUploadFile(null);
                setBulkAssignments([]);
                setBulkResults(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Close
            </button>
            
            {bulkUploadFile && !bulkResults && (
              <button
                onClick={processBulkUpload}
                disabled={isUploading || !bulkUploadFile}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white ${
                  isUploading || !bulkUploadFile
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary-500 hover:bg-primary-600'
                }`}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    <span>Upload and Process</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </Modal>
      
      {/* Group Index Rename Modal */}
      <Modal
        isOpen={isGroupRenameModalOpen}
        onClose={() => {
          setIsGroupRenameModalOpen(false);
          setGroupRenameFile(null);
          setGroupIndexMappings([]);
          setRenameResults(null);
          if (groupRenameFileRef.current) {
            groupRenameFileRef.current.value = '';
          }
        }}
        title="Rename Group Numbers"
        width="90vw"
        maxWidth="700px"
      >
        <div className="space-y-6 p-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex gap-3 items-start">
              <FileText className="text-blue-600 mt-0.5 flex-shrink-0" size={18} />
              <div>
                <p className="text-blue-700 font-medium">Instructions</p>
                <p className="text-blue-600 text-sm mt-1">
                  Download the Excel template, enter new index names for groups, and upload the file.
                  This will change the group number displayed in the UI (e.g., "Group 1" could become "Group 4-B").
                </p>
                <ul className="list-disc list-inside text-sm text-blue-600 mt-2">
                  <li>The template includes all existing groups and their current indices</li>
                  <li>Fill in the "new_index" column with the desired group name/number</li>
                  <li>You can use text like "4-B" or "Special" as group indices</li>
                  <li>Leave "new_index" empty to keep the current index</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Template Download */}
          <button
            onClick={handleDownloadGroupRenameTemplate}
            className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
          >
            <Download size={18} />
            <span>Download Group Index Template</span>
          </button>

          {/* File Upload */}
          {!renameResults && (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
              <input
                ref={groupRenameFileRef}
                type="file"
                onChange={handleGroupRenameFileChange}
                accept=".xlsx,.xls"
                className="hidden"
              />
              <div className="space-y-2">
                {groupRenameFile ? (
                  <div>
                    <div className="flex items-center justify-center mb-3">
                      <div className="p-2 bg-green-100 rounded-full">
                        <Check className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                    <p className="text-green-600 font-medium">{groupRenameFile.name}</p>
                    <p className="text-sm text-gray-500">
                      {(groupRenameFile.size / 1024).toFixed(2)} KB
                    </p>
                    <div className="mt-2">
                      <button
                        onClick={() => {
                          setGroupRenameFile(null);
                          if (groupRenameFileRef.current) groupRenameFileRef.current.value = '';
                        }}
                        className="text-red-500 text-sm hover:text-red-700"
                      >
                        Remove File
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2">
                      <button
                        type="button"
                        className="font-medium text-primary-600 hover:text-primary-500"
                        onClick={() => groupRenameFileRef.current?.click()}
                      >
                        Select a file
                      </button>{' '}
                      or drag and drop
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Excel files only</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Results Table */}
          {groupIndexMappings.length > 0 && (
            <div className="mt-4 border rounded-lg overflow-hidden">
              <div className="max-h-80 overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Group Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Old Index
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        New Index
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {groupIndexMappings.map((mapping, index) => (
                      <tr key={index} className={mapping.status === 'error' ? 'bg-red-50' : (mapping.status === 'success' ? 'bg-green-50' : '')}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="truncate max-w-xs" title={mapping.groupName}>
                            {mapping.groupName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {mapping.oldIndex}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {mapping.newIndex}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {mapping.status === 'pending' ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              Pending
                            </span>
                          ) : mapping.status === 'success' ? (
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                              Success
                            </span>
                          ) : (
                            <span title={mapping.message} className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                              Error: {mapping.message}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Summary Results */}
          {renameResults && (
            <div className={`p-4 rounded-lg ${
              renameResults.error > 0 ? 'bg-yellow-50' : 'bg-green-50'
            }`}>
              <h4 className={`font-medium ${
                renameResults.error > 0 ? 'text-yellow-700' : 'text-green-700'
              }`}>
                Group Number Update Complete
              </h4>
              <div className="mt-2 space-y-1">
                <p className="text-sm">
                  <span className="font-medium">Total:</span> {renameResults.total} groups
                </p>
                <p className="text-sm text-green-700">
                  <span className="font-medium">Success:</span> {renameResults.success} groups
                </p>
                {renameResults.error > 0 && (
                  <p className="text-sm text-red-700">
                    <span className="font-medium">Failed:</span> {renameResults.error} groups
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => {
                setIsGroupRenameModalOpen(false);
                setGroupRenameFile(null);
                setGroupIndexMappings([]);
                setRenameResults(null);
                if (groupRenameFileRef.current) {
                  groupRenameFileRef.current.value = '';
                }
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Close
            </button>
            
            {groupRenameFile && !renameResults && (
              <button
                onClick={processGroupIndexRename}
                disabled={isRenamingGroups || !groupRenameFile}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white ${
                  isRenamingGroups || !groupRenameFile
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-primary-500 hover:bg-primary-600'
                }`}
              >
                {isRenamingGroups ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Upload size={18} />
                    <span>Upload and Update</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};